import { Box, HStack, Input, useToken } from '@chakra-ui/react';
import parsePhoneNumber, {
  CountryCode,
  getCountryCallingCode,
} from 'libphonenumber-js';
import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Select, { GroupBase, StylesConfig } from 'react-select';

import useSelectStyles from '@/components/pages/MyAvailability/molecules/selectStyles';
import useBreakpoint from '@/hooks/useBreakpoint';
import countryCodes from '@/utils/countryCodes';

interface PhoneInputProps {
  disabled: boolean;
  defaultCountry?: CountryCode;
  value: string;
  onChange: (event: string | ChangeEvent<Element>) => void;
  onBlur: () => void;
  isError: boolean;
}

type CountryOption = Record<'label' | 'value', string>;

const countriesWithCountryCodes: CountryOption[] = Object.entries(
  countryCodes,
).map(([countryCode, countryName]) => ({
  label: `(+${getCountryCallingCode(
    countryCode as CountryCode,
  )}) ${countryName}`,
  value: countryCode,
}));

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      disabled,
      value,
      onChange,
      onBlur,
      isError,
      defaultCountry,
    }: PhoneInputProps,
    ref,
  ) => {
    const isLargerThanBreakpoint = useBreakpoint();
    const { t } = useTranslation();

    const [gray200, red500] = useToken('colors', ['gray.200', 'red.500']) as [
      string,
      string,
    ];

    const styles = useSelectStyles<CountryOption>({ isLargerThanBreakpoint });

    const stylesPhoneInput: StylesConfig<
      CountryOption,
      false,
      GroupBase<CountryOption>
    > = {
      ...styles,
      control: (baseStyles, state) => {
        const controlStyles = styles.control?.(baseStyles, state);
        return {
          ...controlStyles,
          borderRadius: 0,
          borderWidth: isError ? 2 : 1,
          borderColor: isError ? red500 : gray200,
          height: isLargerThanBreakpoint ? '3.125rem' : '2.5rem',
        };
      },
      container: (baseStyles, state) => {
        const containerStyles = styles.container?.(baseStyles, state);
        return {
          ...containerStyles,
          minWidth: '200px',
        };
      },
    };

    const [countryCode, setCountryCode] = useState<string>('GB');
    const [phoneNumber, setPhoneNumber] = useState<string>('');

    const valueObject = useMemo(
      () =>
        countriesWithCountryCodes.find(
          (country) => country.value === countryCode,
        ),
      [countryCode],
    );

    useEffect(() => {
      if (value) {
        const newValue = parsePhoneNumber(value || '');

        if (newValue?.isValid) {
          if (newValue.country) {
            setCountryCode(newValue.country);
          }
          setPhoneNumber(newValue.nationalNumber || '');
        }
      }
    }, [value]);

    useEffect(() => {
      if (defaultCountry) {
        setCountryCode(defaultCountry);
      }
    }, [defaultCountry]);

    const onPhoneNumberChange = useCallback(
      (newPhoneNumber: string) => {
        const phoneNumberObject = parsePhoneNumber(
          newPhoneNumber,
          countryCode as CountryCode,
        );

        if (phoneNumberObject?.number) {
          onChange(phoneNumberObject?.number);
        } else if (newPhoneNumber) {
          onChange(
            `+${getCountryCallingCode(
              countryCode as CountryCode,
            )}${newPhoneNumber}`,
          );
        } else {
          onChange('');
        }
      },
      [countryCode, onChange],
    );

    return (
      <HStack spacing={0}>
        <Select
          onChange={(option: CountryOption | null) => {
            if (typeof option?.value === 'string') {
              setCountryCode(option?.value);
              onPhoneNumberChange(phoneNumber);
            }
          }}
          menuPortalTarget={document.body} // to add z-index https://stackoverflow.com/questions/55830799/how-to-change-zindex-in-react-select-drowpdown
          menuShouldScrollIntoView={false}
          menuShouldBlockScroll
          isDisabled={disabled}
          styles={stylesPhoneInput}
          isSearchable
          value={valueObject}
          onBlur={onBlur}
          placeholder={t('country_code')}
          options={countriesWithCountryCodes}
          // eslint-disable-next-line react/no-unstable-nested-components
          formatOptionLabel={({ label, value: option }: CountryOption) => (
            <>
              {typeof option === 'string' && (
                <Box className={`fi fi-${option?.toLowerCase()}`} mr="2" />
              )}
              {label}
            </>
          )}
        />
        <Input
          name="phoneNumberNoCountry"
          variant="rectangular"
          borderColor={isError ? 'red.500' : 'gray.200'}
          borderWidth={isError ? 2 : 1}
          borderLeftWidth="0"
          h={isLargerThanBreakpoint ? '3.125rem' : '2.5rem'}
          disabled={disabled}
          onBlur={onBlur}
          ref={ref}
          placeholder={t('phone_number') || ''}
          value={phoneNumber}
          onChange={(event) => {
            setPhoneNumber(event.target.value);
            onPhoneNumberChange(event.target.value);
          }}
        />
      </HStack>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';

PhoneInput.defaultProps = {
  defaultCountry: undefined,
};

export default PhoneInput;
